import React, { useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { validateForm } from "utils/validation";
import Spinner from "Common/Globle/Spinner";
import fields from "../json/AssignFields.json";
import useFormChange from "Hooks/useFormChange";
import useFormSubmit from "Hooks/useFormSubmit";
import PageBody from "Common/Custom/PageBody";
import { HeadingOne } from "Common/Custom/HeadingOne";
import useFetchAllData from "Hooks/useFetchAllData";
import {
    assignEmployees,
    GetAllRosterList,
    getEmployeesList,
    getAssignedEmployees
} from "adminApi/DutyRosterApi/AssignedEmployeeApi";
import { AssignRules } from "validation/ValidationRules";
import AddAssignedEmployeeForm from "./Componates/AddAssignedEmployeeForm";

const AddAssignedEmployee = () => {
    const navigate = useNavigate();
    const { formFields } = fields;

    // Build initial values object from fields
    const initialValues = useMemo(() => {
        return formFields.reduce((acc, field) => {
            if (field.name === "employee_ids") {
                acc[field.name] = [];
            } else {
                acc[field.name] = "";
            }
            return acc;
        }, {});
    }, [formFields]);

    const {
        values,
        errors,
        handleChange,
        setErrors,
        setValues
    } = useFormChange(initialValues, AssignRules);

    // 🔹 Fetch already assigned employees when roster selection changes
    useEffect(() => {
        if (values.roster_id) {
            const fetchAssignedData = async () => {
                const res = await getAssignedEmployees(values.roster_id);
                if (res.success && Array.isArray(res.data)) {
                    // Map to array of IDs
                    const assignedIds = res.data.map(emp => emp.employee_id);
                    setValues(prev => ({ ...prev, employee_ids: assignedIds }));
                }
            };
            fetchAssignedData();
        } else {
            // Reset if no roster selected
            setValues(prev => ({ ...prev, employee_ids: [] }));
        }
    }, [values.roster_id, setValues]);

    // Fetch Rosters
    const { data: rosterData, loading: loadingRosters } = useFetchAllData(GetAllRosterList);

    // Fetch Employees
    const { data: employeeData, loading: loadingEmployees } = useFetchAllData(getEmployeesList);

    const rosterOptions = useMemo(() => {
        if (!Array.isArray(rosterData)) return [];
        return rosterData.map(r => ({
            value: r.roster_id,
            label: `${r.shift_name} ( ${r.start_date} to ${r.end_date} )`
        }));
    }, [rosterData]);

    const employeeOptions = useMemo(() => {
        if (!Array.isArray(employeeData)) return [];
        return employeeData.map(e => ({
            value: e.employee_id,
            name: e.employee_name,
            position: e.position_name || 'No Position Found',
            assigned_roster_id: e.assigned_roster_id
        }));
    }, [employeeData]);

    const {
        loading: loadingSubmit,
        handleSubmit
    } = useFormSubmit(
        async (formValues) => {
            return await assignEmployees(formValues);
        },
        (vals) => {
            const err = validateForm(vals, AssignRules);
            setErrors(err);
            return err;
        },
        () => navigate(-1),
        "Employees assigned successfully"
    );

    return (
        <PageBody
            card={true}
            headerchildren={<HeadingOne>Assign Employees to Roster</HeadingOne>}
            onSubmit={(e) => handleSubmit(e, values)}
        >
            {(loadingSubmit || loadingRosters || loadingEmployees) && <Spinner isSpinner={true} />}

            <AddAssignedEmployeeForm
                formFields={formFields}
                values={values}
                errors={errors}
                handleChange={handleChange}
                setValues={setValues}
                rosterOptions={rosterOptions}
                employeeOptions={employeeOptions}
                loadingSubmit={loadingSubmit}
            />
        </PageBody >
    );
};

export default AddAssignedEmployee;

